import { defineStore } from 'pinia'
import { UserStore } from '@/front/stores/user_store.js'
import { VideoStore } from '@/front/stores/video_store.js'
import { PlayerStore } from '@/front/stores/player_store.js'
import { AnnStore } from '@/front/stores/ann_store.js'
import { ClickStore } from '@/front/stores/click_store.js'
import { useRoute } from 'vue-router'
import { BrowserHelper } from '@/front/composables/BrowserHelper.js'
import { MessageComposer } from '@/front/composables/MessageComposer.js'
import { isNotEmpty } from '@/front/composables/GeneralHelper.js'
import { getUrlParameter } from '@/front/composables/UxComposer'

import { stringToQueryObject, objectToQueryString } from '@/front/composables/StringComposer';

export const PlaybackStore = defineStore('playback', {
  state: () => {
    return {
      browser: null, // browser object
      captions: null, // captions label being used if any
      error: null,
      id: null, // playback id
      view_id: null, // view id
      intervalTimer: null,
      lastTime: null, // last second updated,
      lastUpdate: null, // last update time
      lastUpdatePosition: null, // last update timestamp of video
      latitude: null,
      loading: false,
      longitude: null,
      manager: false,
      messages: [], // array of messages sent to the window
      params: null, // url arguments at initialization
      plink_id: null, // plink id
      progressPoints: [], // array of progress points
      refer_video: null, // token of video that branched to this playback
      refer_time: null, // time of the video that branched to this playback
      screenHeight: null, // screen height
      screenWidth: null, // screen width
      seconds: [], // watched seconds
      started: false,
      trackingInterval: null, // interval to track playback
      url: null, // url of the playback
      userInteractions: [], // array of user interactions
      variables: {}, // variables to be used in the playback
      viewer: {}, // viewer object
      afkTimer: null,
      afkTimerAmount: 0,
      errorCount: 0,
      updateErrorNotified: false,

    }
  },

  actions: {
    async update(finished = false) {
      // log.info(1,"🔎 PlaybackStore update", finished)
      const playerStore = PlayerStore()
      if (this.errorCount >= 5) {
        log.error(1,"🔴 PlaybackStore update disabled after repeated failures", this.error)
        clearTimeout(this.intervalTimer)
        this.intervalTimer = null
        if (!this.updateErrorNotified) {
          const requestId = this.error?.requestId ? ` Ref: ${this.error.requestId}.` : ''
          alert(`Something went wrong. Please refresh the page to ensure you are using the latest version.${requestId}`)
          this.updateErrorNotified = true
        }
        return
        // window.location.reload()
      }
      // Skip paused updates unless finished or haven't updated in 2 minutes
      if (!finished && (this.lastUpdate && (new Date() - this.lastUpdate) < 5000) || (this.lastUpdatePosition && this.lastUpdatePosition == playerStore.fulltime)) {
        log.info(1,"🔴 PlaybackStore update skipped - paused or last update too recent")
        return
      }

      // log.info(1,"🔎 PlaybackStore proceeeding update", this.lastUpdatePosition, playerStore.fulltime)
      this.loading = true
      this.lastUpdatePosition = playerStore.fulltime
      let endpoint = `/plays/${this.id}`
      if (!this.id) {
        // log.info(1,"🔎 PlaybackStore CREATE")
        endpoint = '/plays'
      } else {
        // log.info(1,`🔎 PlaybackStore UPDATE, id: ${this.id}`)
      }

      // log.info(1,this.data)
      const payload = { ...this.data, finished: finished }
      if (!payload.token) {
        log.info(1,"🔴 PlaybackStore update missing token", { path: window?.location?.pathname, refer_video: this.refer_video })
        this.loading = false
        return
      }
      if (!this.started) {
        // log.info(1,"🔎 Not started, skipping update")
        this.loading = false
        return
      }
      return this.Api.post(endpoint, { play: payload }).then((response) => {
        // log.info(1,`✅ Play ${endpoint == '/plays' ? 'CREATED ✅' : 'UPDATED'}`, response)
        this.id = response.data.play.id
        this.view_id = response.data.play.view_id
        this.viewer.id = response.data.play.viewer_id
        this.lastUpdate = new Date()
        this.error = null
        this.errorCount = 0
        this.updateErrorNotified = false
        this.storeViewer()
        this.storePlay(response.data.play)
      }).catch((error) => {
        this.recordUpdateError(error, { endpoint: endpoint, finished: finished, payload: payload })
      }).finally(() => {
        this.loading = false
      })
    },

    buildUpdateErrorDetails(error, context = {}) {
      const responseData = error?.response?.data || {}
      const responseErrors = Array.isArray(responseData?.errors)
        ? responseData.errors
        : responseData?.error
          ? [responseData.error]
          : []

      return {
        message: error?.message,
        endpoint: context.endpoint,
        finished: context.finished,
        playId: this.id,
        token: context.payload?.token,
        status: error?.response?.status,
        code: responseData?.code,
        requestId: responseData?.request_id,
        sentryEventId: responseData?.sentry_event_id,
        errors: responseErrors
      }
    },

    recordUpdateError(error, context = {}) {
      const details = this.buildUpdateErrorDetails(error, context)
      const hasServerResponse = Boolean(error?.response)
      const isNetworkError = !hasServerResponse && (error?.code === 'ERR_NETWORK' || details.message === 'Network Error')
      const isPageHidden = typeof document !== 'undefined' && document.visibilityState === 'hidden'
      const isExpectedUnloadNetworkFailure = isNetworkError && (Boolean(context.finished) || isPageHidden)

      if (isExpectedUnloadNetworkFailure) {
        log.info(1,"🔕 PlaybackStore expected unload network failure skipped", details)
        return
      }

      this.error = details
      this.errorCount++

      log.error(1,"🔴 PlaybackStore update failed", details)

      if (window?.Sentry?.captureException) {
        if (hasServerResponse) {
          window.Sentry.captureException(error, {
            tags: { area: 'playback_update' },
            extra: details
          })
        } else {
          log.info(1,"🔕 PlaybackStore network transport error skipped for Sentry", details)
        }
      }
    },

    setVariable(key, value) {
      // log.info(1,"🔎 PlaybackStore setVariable", key.toUpperCase() + " --> " + value)
      // Treat missing feature flags as enabled; only block when explicitly false.
      const videoStore = VideoStore()
      if (videoStore.video?.features?.variables === false) {
        return
      }
      if (videoStore.video.manage) {
        return
      }
      if (!isNotEmpty(value)) { 
        // log.info(1,"🔎 PlaybackStore setVariable SKIP", key.toUpperCase(), value)
        return
      }
      
      // These variables cannot be set by the user
      const skips = ['VIDEO_ID', 'PREVIEW_TOKEN', 'NOVIEW','GOSTAMP', 'DEBUG', 'DOOT','T','AUTOPLAY']
      if (skips.includes(key.toUpperCase())) {
        // log.info(1,"🔎 PlaybackStore setVariable SKIP", key.toUpperCase(), value)
        return
      }
      if (!this.variables) {
        // In case undefined, set to empty object
        this.variables = {}
      }

      this.variables[key.toUpperCase()] = value
      
      // Why were we doing this?
      // else {
      //   this.variables[key.toUpperCase()] = this.variables[key.toUpperCase()]
      // }
      log.info(1,"🔎 PlaybackStore setVariable", key.toUpperCase(), this.variables[key.toUpperCase()])
      const annStore = AnnStore()
      annStore.handleVariableChange(key.toUpperCase())

      // Handle viewer variables
      if (key.toUpperCase() == 'NAME') {
        log.info(1,"🔎 PlaybackStore set viewer name", value)
        this.viewer.name = value
      } else if (key.toUpperCase() == 'EMAIL') {
        log.info(1,"🔎 PlaybackStore set viewer email", value)
        this.viewer.email = value
      } else if (key.toUpperCase() == 'PHONE') {
        log.info(1,"🔎 PlaybackStore set viewer phone", value)
        this.viewer.phone = value
      } else if (key.toUpperCase() == 'CUSTOM_ID') {
        log.info(1,"🔎 PlaybackStore set viewer custom_id", value)
        this.viewer.custom_id = value
      }

      const { emitMessage } = MessageComposer()
      emitMessage('set_variable', { key: key.toUpperCase(), value: value })
    },

    getVariable(key) {
      if (key === undefined) return null
      return this.variables[key.toUpperCase()]
    },

    resetInteractions() {
      log.info(1,"💟  PlaybackStore resetInteractions", this.userInteractions)
      this.userInteractions = []
      const annStore = AnnStore()
      annStore.resetAnnotationState()
      this.setAutomaticVariables()
      annStore.personalizeAllInteractions()
      if (this.started) {
        annStore.updateAllConditionalStatuses()
      }
    },

    resetViewer() {
      log.info(1,"💟  PlaybackStore resetViewer", this.viewer)
      this.variables = {}
      this.userInteractions = []
      const annStore = AnnStore()
      annStore.resetAnnotationState()
      annStore.personalizeAllInteractions()
      if (this.started) {
        annStore.updateAllConditionalStatuses()
      }
    },


    initialize(route, manager) {
      const videoStore = VideoStore()

      this.viewer = localStorage.getItem('viewer') ? JSON.parse(localStorage.getItem('viewer')) : {}
      log.info(1,"🔎 PlaybackStore initialize viewer", this.viewer)
      this.manager = manager
      // Process browser and URL
      this.screenWidth = window.innerWidth
      this.screenHeight = window.innerHeight
      window.addEventListener('resize', () => {
        this.screenWidth = window.innerWidth
        this.screenHeight = window.innerHeight
      })
      this.browser = BrowserHelper().browserInfo.browser.name + ' ' + BrowserHelper().browserInfo.browser.version
      this.url = document.referrer || window.location.href
      log.info(1,"✅ Initialize ✅ ")

      if (videoStore.video.blocked) {
        log.info(1,"🔴 PlaybackStore initialize blocked")
        return
      }


      // Set org variables first
      Object.entries(videoStore.video.org_variables).forEach(([key, value]) => {
        this.setVariable(key, value)
      })
      // Then set video variables
      Object.entries(videoStore.video.variables).forEach(([key, value]) => {
        this.setVariable(key, value)
      })
      // Then set plink variables
      if (videoStore.video.plink) {
        log.info(1,"🔎 PlaybackStore initialize plink", videoStore.video.plink)
        this.plink_id = videoStore.video.plink.id
        const params = stringToQueryObject(videoStore.video.plink.arguments)
        Object.entries(params).forEach(([key, value]) => {
          log.info(1,"🔎 PlaybackStore initialize plink key", key, value)
          this.setVariable(key, value)
        })
        this.setVariable("NAME", videoStore.video.plink.name)
        this.setVariable("EMAIL", videoStore.video.plink.email)
        this.setVariable("PHONE", videoStore.video.plink.phone)
        this.setVariable("CUSTOM_ID", videoStore.video.plink.custom_id)
      }

      // Object.entries(this.variables).forEach(([key, value]) => {
      //   log.info(1,"💟  PlaybackStore variable", key, value)
      // })
      // log.info(1,"Variables", this.variables)
      // this.viewer.name = this.variables['NAME'] || localStorage.getItem('viewer')?.name
      // this.viewer.email = this.variables['EMAIL'] || localStorage.getItem('viewer')?.email
      // this.viewer.phone = this.variables['PHONE'] || localStorage.getItem('viewer')?.phone
      // this.viewer.custom_id = this.variables['CUSTOM_ID'] || localStorage.getItem('viewer')?.custom_id
      log.info(1,"Viewer", this.viewer)
      this.setVariable('NAME', this.viewer.name)
      this.setVariable('EMAIL', this.viewer.email)
      this.setVariable('PHONE', this.viewer.phone)
      this.setVariable('CUSTOM_ID', this.viewer.custom_id)

      if (videoStore.video.is_resumable && videoStore.video.features.resume) {
        this.findPlay()
      }

      // If session reset, clear viewer
      // Do same if preview mode
      if (videoStore.video.force_session_reset || !!getUrlParameter('preview_token')) {
        log.info(1,"🔎 PlaybackStore initialize session reset 🔴")
        this.viewer = {}
        this.variables = {}
        this.resetViewer()
      } 


      // Then set url variables
      let hasParams = false
      const normalizeQueryValue = (key, value) => {
        if (value == null) return value
        if (Array.isArray(value)) return value.map((item) => normalizeQueryValue(key, item))
        if (key.toLowerCase() !== 'name') return value

        const hash = window.location.hash || ''
        if (!hash.match(/^#\d+;.+/)) return value

        return `${value}&#${hash.slice(1)}`
      }

      const normalizedQueryEntries = route.query
        ? Object.entries(route.query).map(([key, value]) => [key, normalizeQueryValue(key, value)])
        : []

      this.params = normalizedQueryEntries.length
        ? `?${normalizedQueryEntries.map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value ?? '')}`).join('&')}`
        : null

      normalizedQueryEntries.forEach(([key, value]) => {
        hasParams = true
        this.setVariable(key, value)
      })
      log.info(1,"🔎 PlaybackStore initialize variables", JSON.stringify(this.variables))

      const playerStore = PlayerStore()
      if (hasParams && !playerStore.isDebug && !playerStore.isDebugPerformance && !playerStore.isDev) {
        const keepParamKeys = ['plink_id', 'plinkid']
        const plinkQueryPairs = normalizedQueryEntries.flatMap(([key, value]) => {
          if (!keepParamKeys.includes(key.toLowerCase())) return []
          if (Array.isArray(value)) {
            return value
              .filter(v => v != null && `${v}`.trim() !== '')
              .map(v => `${encodeURIComponent(key)}=${encodeURIComponent(v)}`)
          }
          if (value == null || `${value}`.trim() === '') return []
          return [`${encodeURIComponent(key)}=${encodeURIComponent(value)}`]
        })
        const nextUrl = plinkQueryPairs.length > 0
          ? `${route.path}?${plinkQueryPairs.join('&')}`
          : route.path
        log.info(1,"🔎 PlaybackStore History PushState", nextUrl)
        history.pushState(null, '', nextUrl);
      }

      log.info(1,"Variables", this.variables)
    
      // Begin looping updates
    },

    addGtag(trackingId) {
      try {
        log.info(1,"🔎 PlaybackStore addGtag", trackingId)
        if (!trackingId) return;
      
        // Remove existing gtag script
        const existingScripts = document.querySelectorAll('script[src*="googletagmanager.com/gtag/js"]');
        existingScripts.forEach(script => script.remove());
      
        // Clear global gtag and dataLayer
        if (window && window.gtag) {
          delete window.gtag;
        }
        window.dataLayer = [];
      
        // Inject new gtag script
        const script = document.createElement('script');
        script.async = true;
        script.src = `https://www.googletagmanager.com/gtag/js?id=${trackingId}`;
        document.head.appendChild(script);
      
        // Initialize gtag
        window.dataLayer = window.dataLayer || [];
        function gtag() {
          window.dataLayer.push(arguments);
        }
        window.gtag = gtag;
        gtag('js', new Date());
        gtag('config', trackingId);
      } catch (error) {
        log.info(1,"🔴 PlaybackStore addGtag error", error)
      }
    },

    addFacebookPixel(pixelId) {
      log.info(1,"🔎 PlaybackStore addFacebookPixel", pixelId)
      if (!pixelId) return;
    
      // Remove existing fb pixel script
      const existingScripts = document.querySelectorAll('script[src*="facebook.net/en_US/fbevents.js"]');
      existingScripts.forEach(script => script.remove());
    
      // Remove global fbq and _fbq
      delete window.fbq;
      delete window._fbq;
    
      // Remove existing <noscript> fallback
      const oldNoscripts = document.querySelectorAll('noscript img[src*="facebook.com/tr?"]');
      oldNoscripts.forEach(img => img.parentNode.remove());
    
      // Inject fresh fb pixel script
      (function(f, b, e, v, n, t, s) {
        if (f.fbq) return; n = f.fbq = function() {
          n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
        };
        f._fbq = n;
        n.push = n; n.loaded = true; n.version = '2.0';
        n.queue = []; t = b.createElement(e); t.async = true;
        t.src = v; s = b.getElementsByTagName(e)[0];
        s.parentNode.insertBefore(t, s);
      })(window, document, 'script', 'https://connect.facebook.net/en_US/fbevents.js');
    
      window.fbq('init', pixelId);
      window.fbq('track', 'PageView');
    
      // Add new <noscript> fallback
      const noscript = document.createElement('noscript');
      const img = document.createElement('img');
      img.height = 1;
      img.width = 1;
      img.style.display = 'none';
      img.src = `https://www.facebook.com/tr?id=${pixelId}&ev=PageView&noscript=1`;
      noscript.appendChild(img);
      document.body.appendChild(noscript);
    },

    beginTracking() {
      const playerStore = PlayerStore()
      if (this.started) {
        log.info(1,"🔎 PlaybackStore beginTracking already started")
        return
      }


      const videoStore = VideoStore()
      if (videoStore.video.manage) {
        // log.info(1,"🔎 PlaybackStore beginTracking SKIP manage")
        return
      }

      if (videoStore.video.safe_facebook_pixel_id) {
        this.addFacebookPixel(videoStore.video.safe_facebook_pixel_id)
      }

      if (videoStore.video.safe_gtag) {
        this.addGtag(videoStore.video.safe_gtag)
      }

      // Reset playback start time if we were resuming
      playerStore.startTime = 0
      this.started = true
      log.info(1,"🔎 PlaybackStore beginTracking");
      window.addEventListener('beforeunload', this.finishTracking)

      let interval = 2000; // Start with 2 seconds
      const maxInterval = 60000; // Max interval is 60 seconds

      const scheduleNextUpdate = () => {
        setTimeout(async () => {
          await this.update();

          // Calculate next interval
          interval = Math.min(interval + 1000, maxInterval);

          // log.info(1,`Next update in ${interval / 1000} seconds`);
          scheduleNextUpdate(); // Schedule the next update
        }, interval);
      };

      // Start the first update after 2 seconds
      this.intervalTimer = setTimeout(() => {
        this.update().then(() => {
          scheduleNextUpdate(); // Begin the backoff loop
        });
      }, 2000);
      
      this.startAfkTimer()

      // this.started = true;
    },

    startAfkTimer() {
      const videoStore = VideoStore()
      const clickStore = ClickStore()
      const playerStore = PlayerStore()
      if (videoStore.video.use_afk) {
        log.info(1,"🔎 PlaybackStore startAfkTimer: " + videoStore.video.use_afk + ": " + videoStore.video.afk_amount)
        clearInterval(this.afkTimer)
        this.afkTimerAmount = videoStore.video.afk_amount
        this.afkTimer = setInterval(() => {
          if (playerStore.playerState.paused || playerStore.playerState.ended || clickStore.hasActiveDialog) {
            this.clearAfkTimer()
            return
          }
          this.afkTimerAmount -= 1
          log.info(1,"🔎 PlaybackStore afkTimerAmount", this.afkTimerAmount)
          if (this.afkTimerAmount <= 0) {
            clickStore.showAfk()
          }
        }, 1000)
      }
    },

    clearAfkTimer() {
      log.info(1,"🔎 PlaybackStore clearAfkTimer")
      clearInterval(this.afkTimer)
      this.afkTimer = null
      this.afkTimerAmount = 0
    },

    finishTracking() {
      log.info(1,"🔎 PlaybackStore finishTracking")
      if (!this.started) {
        log.info(1,"🔴 PlaybackStore return, tracking not started")
        return
      }
      // Stop update interval
      clearTimeout(this.intervalTimer)
      this.intervalTimer = null
      // Remove listener
      window.removeEventListener('beforeunload', this.finishTracking)
      // Update to mark as finished
      this.update(true).then(() => {
        log.info(1,"✅ 🔴 PlaybackStore ended view")
        this.id = null
        this.started = false
        this.seconds = []
        this.lastTime = null
        this.lastUpdate = null

      })
    },

    finishLeadCapture() {
      log.info(1,"🔎 PlaybackStore finishLeadCapture")
      log.info(1,"name", this.getVariable('NAME'), this.viewer.name)
      this.viewer.name = this.getVariable('NAME') || this.viewer.name
      log.info(1,"Resulting name: ", this.viewer.name)
      this.viewer.email = this.getVariable('EMAIL') || this.viewer.email
      this.viewer.phone = this.getVariable('PHONE') || this.viewer.phone
      this.viewer.custom_id = this.getVariable('CUSTOM_ID') || this.viewer.custom_id
      this.saveViewer()
      this.update()
    },

    getInteractionData(video, ann, val) {
      const playerStore = PlayerStore()
      let parent_value = ann.type_of == 'question' ? ann.question_prompt : ann.content || ann.caption || ann.internal_label
      return {
        play_id: this.id,
        view_id: this.view_id,
        viewer_id: this.viewer.id,
        parent_id: ann.id,
        parent_type: ann.type_of,
        parent_value: parent_value,
        token: video.token,
        organization_id: video.organization_id,
        plink_id: this.plink_id,
        video_id: video.id,
        video_title: video.title,
        viewer_name: this.viewer.name,
        viewer_email: this.viewer.email,
        viewer_phone: this.viewer.phone,
        viewer_custom_id: this.viewer.custom_id,
        type_of: 'click',
        value: val,
        time: Math.floor(playerStore.decitime),
        click_action: ann.click_action,
        click_value: ann.click_value,
        url: this.url,
        browser: this.browser,
        screen_width: this.screenWidth,
        screen_height: this.screenHeight,
        params: this.params == "?" ? null : this.params,
        variables: this.variables,
        group_id: video.group_id,
        series_id: this.variables?.SERIES_ID,
        organization_id: video.organization_id,
        question_type: ann.question_type,
        internal_label: ann.internal_label,
      }
    },

    async safeTrackVideoResponse(video, ann, uploadUrl) {
      if (this.id) {
        this.trackVideoResponse(video, ann, uploadUrl)
      } else {
        this.update().then(() => {
          this.trackVideoResponse(video, ann, uploadUrl)
        })
      }
    },

    async safeTrackComment(video, comment) {
      if (this.id) {
        this.trackComment(video, comment)
      } else {
        this.update().then(() => {
          this.trackComment(video, comment)
        })
      }
    },

    async trackComment(video, comment) {
      log.info(1,"🔎 PlaybackStore trackComment", video, comment)
      let data = this.getInteractionData(video, {}, comment)
      data.type_of = 'comment'
      this.emitInteraction(data)
      this.Api.post('/reporting/interactions', { interaction: data }).then((response) => {
        log.info(1,"✅ Interaction saved", response)
        this.storeUserInteraction(response.data.interaction)
      }).catch((error) => {
        log.info(1,"🔴 PlaybackStore trackComment error", error)
      })
    },

    async trackVideoResponse(video, ann, uploadUrl) {
      log.info(1,"🔎 PlaybackStore trackVideoResponse", video, ann, uploadUrl)
      if (!this.started) {
        log.info(1,"🔎 PlaybackStore trackVideoResponse not started, skipping")
        return
      } else {
        log.info(1,"🔎 PlaybackStore trackVideoResponse videoStore.video", video, video.manage)
      }
      // Track clicks for buttons, hotspots, images, text, etc
      log.info(1,"🔎 PlaybackStore trackVideoResponse", ann)
      let data = this.getInteractionData(video, ann, uploadUrl)
      log.info(1,"🔎 PlaybackStore trackVideoResponse data", data)
      this.Api.post('/reporting/interactions/video_response', { interaction: data }).then((response) => {
        log.info(1,"✅ Interaction saved", response)
        this.storeUserInteraction(response.data.interaction)
      }).catch((error) => {
        log.info(1,"🔴 PlaybackStore trackVideoResponse error", error)
      })

    },

    async safeTrackDrawingResponse(video, ann, uploadUrl) {
      if (this.id) {
        this.trackDrawingResponse(video, ann, uploadUrl)
      } else {
        this.update().then(() => {
          this.trackDrawingResponse(video, ann, uploadUrl)
        })
      }
    },

    async trackDrawingResponse(video, ann, content) {
      log.info(1,"🔎 PlaybackStore trackDrawingResponse", video, ann, content)
      if (!this.started) {
        log.info(1,"🔎 PlaybackStore trackDrawingResponse not started, skipping")
        return
      } else {
        log.info(1,"🔎 PlaybackStore trackDrawingResponse videoStore.video", video, video.manage)
      }
      // Track clicks for buttons, hotspots, images, text, etc
      log.info(1,"🔎 PlaybackStore trackDrawingResponse", ann)
      let data = this.getInteractionData(video, ann, content)
      log.info(1,"🔎 PlaybackStore trackDrawingResponse data", data)
      this.Api.post('/reporting/interactions/drawing_response', { interaction: data }).then((response) => {
        log.info(1,"✅ Interaction saved", response)
        this.storeUserInteraction(response.data.interaction)
      }).catch((error) => {
        log.info(1,"🔴 PlaybackStore trackDrawingResponse error", error)
      })
    },

    async safeTrackImageResponse(video, ann, uploadUrl) {
      if (this.id) {
        this.trackImageResponse(video, ann, uploadUrl)
      } else {
        this.update().then(() => {
          this.trackImageResponse(video, ann, uploadUrl)
        })
      }
    },

    async trackImageResponse(video, ann, uploadUrl) {
      log.info(1,"🔎 PlaybackStore trackImageResponse", video, ann, uploadUrl)
      if (!this.started) {
        log.info(1,"🔎 PlaybackStore trackImageResponse not started, skipping")
        return
      } else {
        log.info(1,"🔎 PlaybackStore trackImageResponse videoStore.video", video, video.manage)
      }
      // Track clicks for buttons, hotspots, images, text, etc
      log.info(1,"🔎 PlaybackStore trackImageResponse", ann)
      let data = this.getInteractionData(video, ann, uploadUrl)
      log.info(1,"🔎 PlaybackStore trackImageResponse data", data)
      this.Api.post('/reporting/interactions/image_response', { interaction: data }).then((response) => {
        log.info(1,"✅ Interaction saved", response)
        this.storeUserInteraction(response.data.interaction)
      }).catch((error) => {
        log.info(1,"🔴 PlaybackStore trackImageResponse error", error)
      })
    },

    async safeTrackAudioResponse(video, ann, uploadUrl) {
      if (this.id) {
        this.trackAudioResponse(video, ann, uploadUrl)
      } else {
        this.update().then(() => {
          this.trackAudioResponse(video, ann, uploadUrl)
        })
      }
    },

    async trackAudioResponse(video, ann, uploadUrl) {
      log.info(1,"🔎 PlaybackStore trackAudioResponse", video, ann, uploadUrl)
      if (!this.started) {
        log.info(1,"🔎 PlaybackStore trackAudioResponse not started, skipping")
        return
      } else {
        log.info(1,"🔎 PlaybackStore trackAudioResponse videoStore.video", video, video.manage)
      }
      // Track clicks for buttons, hotspots, images, text, etc
      log.info(1,"🔎 PlaybackStore trackAudioResponse", ann)
      let data = this.getInteractionData(video, ann, uploadUrl)

      log.info(1,"🔎 PlaybackStore trackAudioResponse data", data)
      this.Api.post('/reporting/interactions/audio_response', { interaction: data }).then((response) => {
        log.info(1,"✅ Interaction saved", response)
        this.storeUserInteraction(response.data.interaction)

      }).catch((error) => {
        log.info(1,"🔴 PlaybackStore trackAudioResponse error", error)
      })
    },

    async safeTrackViewerClick(video, ann) {

      if (this.id) {
        log.info(1,"🔎 PlaybackStore safeTrackViewerClick id", this.id)
        this.trackViewerClick(video, ann)
      } else {
        log.info(1,"🔎 PlaybackStore safeTrackViewerClick update THEN track click")
        this.update().then(() => {
          log.info(1,"🔎 PlaybackStore safeTrackViewerClick update is done, now track")
          this.trackViewerClick(video, ann)
        })
      }
    },

    trackViewerClick(video, ann) {
      const videoStore = VideoStore()
      if (videoStore.video.manage) {
        log.info(1,"🔎 PlaybackStore trackViewerClick manage, skipping")
        return
      } else {
        log.info(1,"🔎 PlaybackStore trackViewerClick videoStore.video", video, video.manage)
      }
      // Track clicks for buttons, hotspots, images, text, etc
      log.info(1,"🔎 PlaybackStore trackViewerClick", ann)
      let val = ann.transformed_content || ann.content || ann.transformed_caption || ann.caption || ann.extra
      let data = this.getInteractionData(video, ann, val)
      log.info(1,"🔎 PlaybackStore trackViewerClick data", data)
      this.emitInteraction(data)
      this.Api.post('/reporting/interactions', { interaction: data }).then((response) => {
        log.info(1,"✅ Interaction saved", response)
        this.storeUserInteraction(response.data.interaction)

      }).catch((error) => {
        log.info(1,"🔴 PlaybackStore trackViewerClick error", error)
      })
    },

    async safeTrackResponse(video, ann, response, correctness = null) {
      const videoStore = VideoStore()
      if (videoStore.video.manage) {
        log.info(1,"🔎 PlaybackStore safeTrackResponse manage, skipping")
        return
      } else {
        log.info(1,"🔎 PlaybackStore safeTrackResponse videoStore.video", video, video.manage)
      }
      log.info(1,"🔎 PlaybackStore safeTrackResponse", ann, response, correctness)

      if (this.id) {
        this.trackResponse(video, ann, response, correctness)
      } else {
        this.update().then(() => {
          this.trackResponse(video, ann, response, correctness)
        })
      }
    },

    async trackResponse(video, ann, response, correctness = null) {
      log.info(1,"🔎 PlaybackStore trackResponse", ann, response)
      let data = this.getInteractionData(video, ann, response)
      data.type_of = 'reply'
      if (correctness && correctness.correctValue) {
        log.info(1,"🔎 PlaybackStore trackResponse correctness", correctness)
        data.correct = correctness.isCorrect
        data.correct_answer = correctness.correctValue
      }
      this.emitInteraction(data)
      this.storeUserInteraction(data)
      log.info(1,"🔎 PlaybackStore trackResponse data", data)
      this.Api.post('/reporting/interactions', { interaction: data }).then((response) => {
        log.info(1,"✅ Interaction saved", response)
        // this.storeUserInteraction(response.data.interaction)
      }).catch((error) => {
        log.info(1,"🔴 PlaybackStore trackResponse error", error)
      })
    },

    storeUserInteraction(int) {
      log.info(1,"🔎 PlaybackStore storeUserInteraction", int)
      this.userInteractions.unshift(int)
      let ints = this.getLocalStorageInteractions()
      ints.unshift(int)
      const maxStoredInteractions = 1000
      this.safeLocalStorageSetItem('MS_INTERACTIONS', JSON.stringify(ints.slice(0, maxStoredInteractions)))
      this.setAutomaticVariables()
    },

    setAutomaticVariables() {
      // Set cScore
      let used = []
      let { correctCount, incorrectCount } = this.correctAndIncorrectCounts
      if (correctCount + incorrectCount == 0) {
        this.setVariable('cScore', 0)
        this.setVariable('pScore', 0)
        return
      } else {
        let cScore = Math.round((correctCount / (correctCount + incorrectCount)) * 100)
        log.info(1,"🔎 PlaybackStore setAutomaticVariables cScore", cScore, "correctCount", correctCount, "incorrectCount", incorrectCount, "User Ints count: ", this.userInteractions.length)
        this.setVariable('cScore', cScore)
      }

      // Set pScore
      const annStore = AnnStore()
      let answeredCount = 0
      annStore.questionsWithCorrectness.forEach(question => {
        if (this.userInteractions.find(interaction => interaction.parent_id == question.id)) {
          answeredCount++
        }
      })
      let pScore = Math.round((answeredCount / annStore.questionsWithCorrectness.length) * 100)
      // log.info(1,"🔎 PlaybackStore setAutomaticVariables pScore", pScore)
      this.setVariable('pScore', pScore)
    },

    saveViewer() {
      if (this.viewer.id) {
        let data = {
          id: this.viewer.id,
          name: this.viewer.name,
          email: this.viewer.email,
          phone: this.viewer.phone,
          custom_id: this.viewer.custom_id,
          variables: this.variables
        }
        this.Api.put(`/reporting/viewers/${this.viewer.id}`, { viewer: data }).then((response) => {
          log.info(1,"✅ Viewer saved", response)
          this.storeViewer()
        }).catch((error) => {
          log.info(1,"🔴 PlaybackStore saveViewer error", error)
        })
      } else {
        log.info(1,"🔴 PlaybackStore saveViewer no viewer id")
      }
    },


    handleTimeUpdate(time) {
      // Start tracking if not already started
      if (time > 0 && !this.started) {
        this.beginTracking()
      }

      // Update last time
      const newTime = Math.floor(time)
      if (newTime !== this.lastTime) {
        if (newTime > 2 && newTime % 2 == 0) {
          this.storePlay()
        }
        const videoStore = VideoStore()
        // log.info(1,"🔎 PlaybackStore newTime", newTime)
        this.lastTime = newTime
        this.seconds.push(newTime)
        let percent = Math.floor(100 * (newTime / (videoStore.video.duration || 1)))
        // log.info(1,"🔎 PlaybackStore handleTimeUpdate percent", percent)
        this.handleNewPercentage(percent)
      }
    },

    handleNewPercentage(percent) {
      const { emitMessage, trackGtagEvent } = MessageComposer()
      const videoStore = VideoStore()
      log.info(1,"🔎 PlaybackStore handleNewPercentage", percent)
      const milestones = [10, 25, 50, 75, 90, 95, 100]
      let target = null

      if (percent >= 99) {
        target = 100
      } else if (percent >= 95) {
        target = 95
      } else if (percent >= 90) {
        target = 90
      } else if (percent >= 75) {
        target = 75
      } else if (percent >= 50) {
        target = 50
      } else if (percent >= 25) {
        target = 25
      } else if (percent >= 10) {
        target = 10
      }

      if (!target) {
        return
      }

      const emitProgress = (milestone) => {
        if (this.progressPoints.includes(milestone)) {
          return
        }
        this.progressPoints.push(milestone)
        emitMessage(`progress_${milestone}`)
        trackGtagEvent(`Reached ${milestone}%`, videoStore.video.title, 'Video Player Events')
        if (milestone === 100) {
          const playerStore = PlayerStore()
          setTimeout(() => {
            if (!playerStore.hasTriggeredEnd) {
              emitMessage('video_end')
            }
          }, 500)
        }
      }

      milestones.forEach((milestone) => {
        if (milestone <= target) {
          emitProgress(milestone)
        }
      })
    },

    emitInteraction(data) {
      const { emitMessage } = MessageComposer()
      log.info(1,"🔎 PlaybackStore emit viewer_interaction", data)
      let object = {

        interaction_type: data.type_of,
        interaction_value: data.value,
        internal_label: data.internal_label,
        parent_id: data.parent_id,
        parent_type: data.parent_type,
        parent_value: data.parent_value,
        question_style: data.question_type, // Have to keep using question_style for existing customers
        correct_answer: data.correct_answer,
        correct: data.correct,
      }
      emitMessage('viewer_interaction', object)
    },

    safeLocalStorageSetItem(key, value) {
      try {
        localStorage.setItem(key, value)
        return true
      } catch (error) {
        log.warn(1,`🔴 PlaybackStore localStorage set failed for ${key}`, error?.name, error?.message)
        return false
      }
    },

    isQuotaExceededError(error) {
      return error?.name === 'QuotaExceededError' ||
        error?.name === 'NS_ERROR_DOM_QUOTA_REACHED' ||
        error?.code === 22 ||
        error?.code === 1014
    },

    getLocalStoragePlays() {
      try {
        const raw = JSON.parse(localStorage.getItem('MS_PLAYS') || '[]')
        return Array.isArray(raw) ? raw : []
      } catch (error) {
        log.warn(1,"🔴 PlaybackStore could not parse MS_PLAYS, resetting", error?.message)
        return []
      }
    },

    persistPlaysWithQuotaHandling(plays) {
      const maxStoredPlays = 100
      let candidate = Array.isArray(plays) ? plays.slice(0, maxStoredPlays) : []

      while (candidate.length > 0) {
        try {
          localStorage.setItem('MS_PLAYS', JSON.stringify(candidate))
          return
        } catch (error) {
          if (!this.isQuotaExceededError(error)) {
            log.warn(1,"🔴 PlaybackStore could not persist MS_PLAYS", error?.name, error?.message)
            return
          }
          candidate.pop()
        }
      }

      try {
        localStorage.removeItem('MS_PLAYS')
      } catch (error) {
        log.warn(1,"🔴 PlaybackStore could not clear MS_PLAYS after quota failures", error?.message)
      }
    },

    storeViewer() {
      this.safeLocalStorageSetItem('viewer', JSON.stringify(this.viewer))
      this.safeLocalStorageSetItem('variables', JSON.stringify(this.variables))
    },

    storePlay(play) {
      if (!play) {
        return
      }
      const videoStore = VideoStore()
      // log.info(1,"StorePlay")
      const data = {
        id: play.id,
        title: videoStore.video.title,
        token: play.token,
        seconds: play.seconds,
        variables: play.variables,
        viewer_id: play.viewer_id,
        guestName: play.guestName,
        guestEmail: play.guestEmail,
        guestPhone: play.guestPhone,
        guestID: play.guestID,
        screenWidth: play.screenWidth,
        screenHeight: play.screenHeight,
        refer_video: play.refer_video,
        url: play.url,
        token: play.token,
        params: play.params,
        browser: play.browser,
        latitude: play.latitude,
        longitude: play.longitude,
        captions: play.captions,
        plink_id: play.plink_id,
        created_at: play.created_at,
        updated_at: play.updated_at,
      }
      
      // log.info(1,"🔎 PlaybackStore storePlay " + this.id)
      const plays = this.getLocalStoragePlays()

      // Remove any existing play with same ID before adding new one
      const existingIndex = plays.findIndex(play => play.id === this.id)

      if (existingIndex !== -1) {
        // Token was getting overwritten by state change, so we need to save
        // LATER better solution?
        plays.splice(existingIndex, 1)
      }

      // log.info(1,"🔎 PlaybackStore resumable storePlay data", data)
      plays.unshift(data)
      this.persistPlaysWithQuotaHandling(plays)
    },

    findPlay() {
      log.info(1,"🔎 PlaybackStore findPlay")
      const videoStore = VideoStore()

      // Check for plink play first
      if (videoStore.video.plink && videoStore.video.lastPlay) {
        log.info(1,"🔎 PlaybackStore resumable findPlay plink play", videoStore.video.lastPlay)
        this.existingPlay = videoStore.video.lastPlay
        this.existingInteractions = videoStore.video.lastInteractions
        return 
      } else {
        log.info(1,"🔎 PlaybackStore resumable findPlay no plink play, checking local storage")
      }
      
      var plays = this.getLocalStoragePlays()
      if (plays.length === 0) {
        log.info(1,"🔎 PlaybackStore resumable findPlay no plays in local storage")
        return null
      }


      // If we have an identifier, filter plays by that
      if (this.viewer.email) {
        plays = plays.filter(play => play.guestEmail === this.viewer.email)
      } else {
        plays = plays.filter(play => !play.guestEmail)
      }

      if (this.viewer.phone) {
        plays = plays.filter(play => play.guestPhone === this.viewer.phone)
      } else {
        plays = plays.filter(play => !play.guestPhone)
      }

      if (this.viewer.custom_id) {
        plays = plays.filter(play => play.guestID === this.viewer.custom_id)
      } else {
        plays = plays.filter(play => !play.guestID)
      }

      let play = plays.find(play =>  play.token === videoStore.video.token) || null

      if (play) {
        // log.info(1,"🔎 PlaybackStore resumable found play " + play.id, play)
        // log.info(1,"🔎 PlaybackStore resumable found userInteractions", this.getLocalStorageInteractions().filter(i => i.play_id === play.id).length)
        this.existingPlay = play
        log.info(1,"🔎 PlaybackStore resumable found play " + play.id, JSON.parse(JSON.stringify(play)))
        let searching = true
        let count = 0
        while (searching && count < 100) {
          count++
          log.info(1,`🔎 PlaybackStore resumable findPlay searching with count ${count} for refer_video ${this.existingPlay.token} and created after ${this.existingPlay.updated_at}`)
          let play = plays.find(play => play.refer_video === this.existingPlay.token && Date.parse(play.updated_at) > Date.parse(this.existingPlay.updated_at))
          if (play) {
            log.info(1,"🔎 PlaybackStore resumable found next play from refer_video " + this.existingPlay.token, play)
            this.existingPlay = play
          } else {
            log.info(1,"🔎 PlaybackStore could not find play for refer_video " + this.existingPlay.token + " and created after " + this.existingPlay.updated_at)
            searching = false
          }
        }

        log.info(1,"🔎 PlaybackStore resumable final play: " + this.existingPlay.title + " (" + this.existingPlay.token + ")")
        this.existingInteractions = this.getLocalStorageInteractions().filter(i => i.play_id === play.id)
      } else {
        log.info(1,"🔎 PlaybackStore resumable findPlay no play found, search!")
      }
    },

    getLocalStorageInteractions() {
      try {
        const raw = JSON.parse(localStorage.getItem('MS_INTERACTIONS') || '[]')
        return Array.isArray(raw) ? raw : []
      } catch (error) {
        log.warn(1,"🔴 PlaybackStore could not parse MS_INTERACTIONS, resetting", error?.message)
        return []
      }
    },

    resumeFromPlay() {

      const annStore = AnnStore()
      const playerStore = PlayerStore()
      if (!this.existingPlay) {
        return
      }
      log.info(1,"🔎 PlaybackStore resumeFromPlay existingPlay", this.existingPlay)
      this.id = this.existingPlay.id
      this.viewer.id = this.existingPlay.viewer_id
      this.viewer.name = this.existingPlay.guestName || this.viewer.name
      this.viewer.email = this.existingPlay.guestEmail || this.viewer.email
      this.viewer.phone = this.existingPlay.guestPhone || this.viewer.phone
      this.viewer.custom_id = this.existingPlay.guestID || this.viewer.custom_id
      this.variables = this.existingPlay.variables
      this.refer_video = this.existingPlay.refer_video
      this.seconds = this.existingPlay.seconds
      this.url = this.existingPlay.url
      this.params = this.existingPlay.params
      this.browser = this.existingPlay.browser
      this.latitude = this.existingPlay.latitude
      this.longitude = this.existingPlay.longitude
      this.captions = this.existingPlay.captions
      this.plink_id = this.existingPlay.plink_id

      let start = this.existingPlay.seconds ? this.existingPlay.seconds[this.existingPlay.seconds.length - 1] : 0
      playerStore.startTime = start || 0
      log.info(1,"🔎 PlaybackStore resumeFromPlay seconds", this.existingPlay.seconds)
      log.info(1,"🔎 PlaybackStore resumeFromPlay variables", this.existingPlay.variables)
      log.info(1,"🔎 PlaybackStore resumeFromPlay startTime", playerStore.startTime)

      // Set variables
      if (this.existingPlay.variables) {
      Object.entries(this.existingPlay.variables).forEach(([key, value]) => {
          log.info(1,"🔎 PlaybackStore resumeFromPlay set variable", key, value)
          this.setVariable(key, value)
        })
      }

      // Set clicks from user interactions for this play
      log.info(1,"🔎 PlaybackStore resumeFromPlay ints", this.existingInteractions)
      annStore.setExistingInteractions(this.existingInteractions)
    },

    clearExistingPlay() {
      this.existingPlay = null
    },

    handlePaymentSuccess(paymentIntent) {
      const videoStore = VideoStore()
      log.info(1,"🔎 PlaybackStore handlePaymentSuccess")
      log.info(1,"🔎 PlaybackStore handlePaymentSuccess", paymentIntent)
      return this.Api.get(`/videos/${videoStore.video.token}/purchase/success?intent_id=${paymentIntent.id}`).then((response) => {
        log.info(1,"🔎 PlaybackStore handlePaymentSuccess response", response)
        if (response.data.plink) {
          this.plink_id = response.data.plink.id
        }
        return response
      }).catch((error) => {
        log.info(1,"🔴 PlaybackStore handlePaymentSuccess error", error)
      })
    },
  },

  getters: {
    data: (state) => {
      const videoStore = VideoStore()
      const data = {
        seconds: state.seconds,
        variables: state.variables,
        viewer_id: state.viewer?.id,
        guestName: state.viewer?.name,
        guestEmail: state.viewer?.email,
        guestPhone: state.viewer?.phone,
        guestID: state.viewer?.custom_id,
        custom_id: state.viewer?.custom_id,
        screenWidth: state.screenWidth,
        screenHeight: state.screenHeight,
        refer_video: state.refer_video,
        url: state.url,
        token: videoStore.video?.token,
        params: state.params,
        browser: state.browser,
        latitude: state.latitude,
        longitude: state.longitude,
        captions: state.captions,
        plink_id: state.plink_id,
        series_id: state.variables?.SERIES_ID
      }
      return data
    },
    correctAndIncorrectCounts: (state) => {
      let used = []
      let correctCount = 0
      let incorrectCount = 0
      state.userInteractions.reverse().forEach(interaction => {
        if (!interaction.correct_answer) {
          return
        }
        if (used.includes(interaction.parent_id)) {
          return
        }
        used.push(interaction.parent_id)
        if (interaction.correct) {
          correctCount++
        } else {
          incorrectCount++
        }
      })
      return { correctCount, incorrectCount }
    },
  }
})
